import { useEffect, useRef } from "react";
import { SERVICES, MATERIAL_TAGS } from "@/data/services";
import {
  Hammer, LayoutPanelLeft, Grid3x3, Square, PaintRoller, Droplets, Home, ChevronRight, Layers, Wrench, Droplet, Zap, ShowerHead, Settings
} from "lucide-react";

const ICONS = {
  Hammer, LayoutPanelLeft, Brick: Layers, Trowel: Wrench, Grid3x3, Square, PaintRoller, Droplets, Home, Droplet, Zap, ShowerHead, Settings,
};

export default function Step1({ selectedServiceId, selectedItemId, onSelect }) {
  const selectedService = SERVICES.find(s => s.id === selectedServiceId);
  const itemsRef = useRef(null);

  useEffect(() => {
    if (selectedService && itemsRef.current) {
      setTimeout(() => {
        itemsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [selectedServiceId]);

  return (
    <div className="space-y-8 animate-fade-up" data-testid="calc-step-1">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#004B87] font-semibold mb-2">Paso 1</div>
        <h2 className="font-display font-semibold text-2xl md:text-3xl text-slate-900 tracking-tight">
          ¿Qué tipo de trabajo necesitas?
        </h2>
        <p className="text-slate-500 mt-2 text-sm">Selecciona la categoría que mejor encaje. Luego eliges la partida concreta.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {SERVICES.map(s => {
          const Icon = ICONS[s.icon] || Hammer;
          const isActive = selectedServiceId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s.id, null)}
              data-testid={`service-card-${s.id}`}
              className={[
                "group text-left p-4 md:p-5 rounded-xl border bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                isActive ? "border-[#004B87] ring-2 ring-[#004B87]/10 bg-[#E6F0FA]/40" : "border-slate-200 hover:border-[#004B87]/50"
              ].join(" ")}
            >
              <div className={[
                "w-11 h-11 rounded-lg grid place-items-center mb-3 transition-colors",
                isActive ? "bg-[#004B87] text-white" : "bg-slate-100 text-slate-700 group-hover:bg-[#E6F0FA] group-hover:text-[#004B87]"
              ].join(" ")}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-display font-semibold text-slate-900 text-[15px] leading-tight">{s.name}</div>
              <div className="text-xs text-slate-500 mt-1.5 leading-snug">{s.description}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] mt-3 font-semibold text-slate-400">
                {s.items.length} partidas
              </div>
            </button>
          );
        })}
      </div>

      {selectedService && (
        <div ref={itemsRef} className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 md:p-6 animate-fade-up scroll-mt-20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#004B87] font-semibold">Elige la partida</div>
              <div className="font-display font-semibold text-lg text-slate-900">{selectedService.name}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {selectedService.items.map(item => {
              const isActive = selectedItemId === item.id;
              const matTag = item.material ? MATERIAL_TAGS[item.material] : null;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(selectedService.id, item.id)}
                  data-testid={`service-item-${item.id}`}
                  className={[
                    "flex items-start justify-between gap-3 p-3.5 rounded-lg border text-left transition-all",
                    isActive ? "border-[#004B87] bg-white ring-2 ring-[#004B87]/10" : "border-slate-200 bg-white hover:border-[#004B87]/50"
                  ].join(" ")}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 leading-tight">{item.name}</div>
                    <div className="text-xs text-slate-500 mt-1">desde <strong className="text-slate-700">{item.price} €/{item.unit}</strong></div>
                    {matTag && (
                      <span className={"inline-block mt-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border " + matTag.color}>
                        {matTag.label}
                      </span>
                    )}
                  </div>
                  <ChevronRight className={"w-4 h-4 shrink-0 mt-1 " + (isActive ? "text-[#004B87]" : "text-slate-400")} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
